import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCards, reviewCard, Card } from '../api';

export default function FlashcardPage() {
  const { id } = useParams<{ id: string }>();
  const deckId = Number(id);

  const [cards, setCards] = useState<Card[]>([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [reviewed, setReviewed] = useState(0);

  useEffect(() => {
    getCards(deckId).then(data => {
      // Shuffle cards
      const shuffled = [...data].sort(() => Math.random() - 0.5);
      setCards(shuffled);
      setLoading(false);
    });
  }, [deckId]);

  const currentCard = cards[index];
  const isJa = currentCard?.front?.match(/[\u3040-\u30ff\u4e00-\u9faf]/);

  const handleFlip = () => setFlipped(f => !f);

  const handleQuality = useCallback(async (quality: number) => {
    if (!currentCard) return;
    await reviewCard(currentCard.id, quality);
    setReviewed(r => r + 1);
    setFlipped(false);
    setTimeout(() => {
      if (index + 1 >= cards.length) {
        setDone(true);
      } else {
        setIndex(i => i + 1);
      }
    }, 100);
  }, [currentCard, index, cards.length]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleFlip(); }
      if (flipped) {
        if (e.key === '1') handleQuality(1);
        if (e.key === '2') handleQuality(3);
        if (e.key === '3') handleQuality(4);
        if (e.key === '4') handleQuality(5);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [flipped, handleQuality]);

  if (loading) return <div className="loading"><div className="spinner" />Đang tải thẻ...</div>;
  if (cards.length === 0) return (
    <div className="flashcard-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="empty-state"><div className="empty-icon">🃏</div><p>Bộ từ chưa có thẻ nào.</p></div>
    </div>
  );

  if (done) return (
    <div className="flashcard-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="quiz-result">
        <div className="result-emoji">🎉</div>
        <div className="result-score">{reviewed}</div>
        <div className="result-label">thẻ đã ôn xong</div>
        <div className="result-actions">
          <button className="btn btn-jade btn-lg" onClick={() => { setIndex(0); setDone(false); setReviewed(0); setCards(c => [...c].sort(() => Math.random() - 0.5)); }}>
            🔄 Học lại
          </button>
          <Link to={`/deck/${deckId}`} className="btn btn-ghost btn-lg">← Về bộ từ</Link>
        </div>
      </div>
    </div>
  );

  const progress = ((index) / cards.length) * 100;

  return (
    <div className="flashcard-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>

      <div className="flashcard-progress">
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
        <div className="progress-text">{index + 1} / {cards.length}</div>
      </div>

      {/* Card */}
      <div className="flashcard-container" onClick={handleFlip}>
        <div className={`flashcard-inner ${flipped ? 'flipped' : ''}`}>
          {/* Front */}
          <div className="flashcard-face front">
            <div className="card-hint">Từ cần học</div>
            <div className="card-front-text">{currentCard.front}</div>
            {isJa && currentCard.reading && (
              <div className="card-reading">{currentCard.reading}</div>
            )}
            <div className="flip-hint">Nhấn để lật thẻ · Space</div>
          </div>
          {/* Back */}
          <div className="flashcard-face back">
            <div className="card-hint">Nghĩa</div>
            <div className="card-back-text">{currentCard.back}</div>
            {currentCard.example && (
              <div className="card-example">{currentCard.example}</div>
            )}
            <div className="flip-hint">Lật lại · Space</div>
          </div>
        </div>
      </div>

      {/* Quality buttons — only show after flip */}
      {flipped && (
        <div className="quality-section">
          <h4>Bạn nhớ tốt đến đâu?</h4>
          <div className="quality-buttons">
            <button className="quality-btn q-again" onClick={() => handleQuality(1)}>
              😵 Quên<span>Ôn lại ngay · 1</span>
            </button>
            <button className="quality-btn q-hard" onClick={() => handleQuality(3)}>
              😓 Khó<span>Ôn sớm · 2</span>
            </button>
            <button className="quality-btn q-good" onClick={() => handleQuality(4)}>
              😊 Nhớ<span>Tốt · 3</span>
            </button>
            <button className="quality-btn q-easy" onClick={() => handleQuality(5)}>
              🚀 Dễ<span>Rất tốt · 4</span>
            </button>
          </div>
        </div>
      )}

      <p style={{ textAlign: 'center', color: 'var(--ink-muted)', fontSize: '0.75rem', marginTop: '1rem' }}>
        Phím tắt: Space = lật thẻ · 1/2/3/4 = đánh giá
      </p>
    </div>
  );
}
